/**
 * Play muted loop videos only when they enter the viewport (not on page load
 * for off-screen videos). Uses data-autoplay-on-view — no HTML autoplay attribute
 * so the browser does not start playback until JS runs play() on intersect.
 */
(() => {
  const SELECTOR = 'video[data-autoplay-on-view]';
  const INIT_ATTR = 'data-autoplay-init';

  const canAttemptPlay = (video) => {
    if (!video) return false;
    if (video.hasAttribute('data-no-autoplay')) return false;
    return true;
  };

  const configureForAutoplay = (video) => {
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.muted = true;
    video.playsInline = true;
    if (!video.hasAttribute('loop')) video.loop = true;
  };

  const attemptPlay = (video) => {
    if (!canAttemptPlay(video)) return;
    configureForAutoplay(video);
    const playPromise = video.play?.();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  };

  const initVideo = (video) => {
    if (!video || video.hasAttribute(INIT_ATTR)) return;
    video.setAttribute(INIT_ATTR, 'true');

    configureForAutoplay(video);

    let inView = false;

    const onCanPlay = () => {
      if (inView) attemptPlay(video);
    };
    video.addEventListener('canplay', onCanPlay, { passive: true });
    video.addEventListener('loadeddata', onCanPlay, { passive: true });

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            inView = entry.isIntersecting;
            if (entry.isIntersecting) {
              attemptPlay(video);
            } else {
              try {
                video.pause();
              } catch (_) {}
            }
          });
        },
        { rootMargin: '100px', threshold: 0.15 }
      );
      observer.observe(video);
    } else {
      const check = () => {
        const rect = video.getBoundingClientRect();
        const visible =
          rect.bottom > 0 &&
          rect.right > 0 &&
          rect.top < (window.innerHeight || document.documentElement.clientHeight) &&
          rect.left < (window.innerWidth || document.documentElement.clientWidth);
        if (visible) attemptPlay(video);
      };
      window.addEventListener('scroll', check, { passive: true });
      check();
    }
  };

  const scan = (root = document) => {
    root.querySelectorAll(SELECTOR).forEach(initVideo);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  } else {
    scan();
  }

  document.addEventListener('shopify:section:load', (e) => scan(e.target || document));
  document.addEventListener('shopify:block:select', (e) => scan(e.target || document));
})();
